import React, { memo, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import {
  Checkbox,
  FormControlLabel,
  Radio,
  Typography,
  generateUtilityClass,
  unstable_composeClasses as composeClasses,
  useThemeProps,
  generateUtilityClasses,
} from '@mui/material';
import { useRadioGroup } from '@mui/material/RadioGroup';
import { Check } from '@mui/icons-material';
import clsx from 'clsx';

import { useControlled } from '@mui/material/utils';
import RadioIcon from '../RadioIcon';

const PREFIX = 'MuiSwitchChip';

export function getSwitchChipUtilityClass(slot) {
  return generateUtilityClass(PREFIX, slot);
}

const useUtilityClasses = (ownerState) => {
  const { classes } = ownerState;

  const slots = {
    root: ['root'],
    fullWidth: ['fullWidth'],
    disabled: ['disabled'],
    checked: ['checked'],
    inactive: ['inactive'],
    label: ['label'],
    labelSecondary: ['labelSecondary'],
    control: ['control'],
    controlRadio: ['control', 'controlRadio'],
    icon: ['icon'],
    checkIcon: ['icon', 'checkIcon'],
    hideControl: ['hideControl'],
  };

  return composeClasses(slots, getSwitchChipUtilityClass, classes);
};

const switchChipClasses = generateUtilityClasses(PREFIX, [
  'root',
  'fullWidth',
  'disabled',
  'checked',
  'inactive',
  'labelSecondary',
  'control',
  'controlRadio',
  'icon',
  'checkIcon',
  'hideControl',
]);

const Root = styled(FormControlLabel, {
  name: PREFIX,
  slot: 'Root',
  overridesResolver: (props, styles) => {
    const { ownerState } = props;

    return [
      styles.root,
      ownerState.fullWidth && styles.fullWidth,
      ownerState.disabled && styles.disabled,
      ownerState.checked && styles.checked,
      ownerState.inactive && styles.inactive,
      ownerState.hideControl && styles.hideControl,
    ];
  },
})(({ theme }) => ({
  /* Styles applied to the root element. */
  padding: '7px 12px 7px 16px',
  margin: 0,
  border: `1px solid ${theme.palette.action.disabled}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: '#fff',
  transition: 'background-color 0.3s ease',
  minHeight: '34px',

  [`&.${switchChipClasses.checked}`]: {
    backgroundColor: theme.palette.primary.light,
    borderColor: 'transparent',

    '& .MuiFormControlLabel-label': {
      fontWeight: 'bold',
      letterSpacing: 'normal',
      backgroundColor: 'transparent',
    },
  },

  [`&.${switchChipClasses.disabled}`]: {
    color: '#b4b4b4',
    backgroundColor: theme.palette.action.disabledBackground,
    borderColor: '#b4b4b4',

    [`& .${switchChipClasses.control}`]: {
      color: 'inherit',
    },

    '& .MuiFormControlLabel-label': {
      color: 'inherit',
    },
  },

  [`&.${switchChipClasses.fullWidth}`]: {
    width: '100%',
  },

  /* Styles applied to the label's Typography component. */
  '& .MuiFormControlLabel-label': {
    marginRight: 'auto',
    userSelect: 'none',
    lineHeight: '1.2',
    padding: 0,
    marginLeft: 0,
    letterSpacing: '0.2px',
  },

  [`& .${switchChipClasses.labelSecondary}`]: {
    display: 'block',
    fontWeight: 'inherit',
  },

  [`& .${switchChipClasses.inactive}`]: {
    pointerEvents: 'none',
  },

  [`& .${switchChipClasses.control}`]: {
    padding: '0',
    alignSelf: 'center',

    [`& .${switchChipClasses.checkIcon}`]: {
      visibility: 'hidden',
    },

    [`&.${switchChipClasses.checked}`]: {
      [`& .${switchChipClasses.checkIcon}`]: {
        visibility: 'visible',
      },
    },
  },

  [`& .${switchChipClasses.controlRadio}`]: {
    // alignSelf: 'flex-start',
    marginLeft: '-6px',
    marginRight: '10px',
  },

  [`& .${switchChipClasses.icon}`]: {
    fontSize: '22px',
  },

  [`& .${switchChipClasses.hideControl}`]: {
    [`& .${switchChipClasses.control}`]: {
      display: 'none',
    },
  },

  [`& .${switchChipClasses.checkIcon}`]: {},
}));

const SwitchChip = memo(function SwitchChip(inProps) {
  /**
   * MUI
   */
  const props = useThemeProps({ props: inProps, name: PREFIX });

  const {
    inactive,
    fullWidth,
    className,
    defaultChecked,
    checked: checkedProp,
    onChange: onChangeProp,
    name: nameProp,
    labelPlacement: labelPlacementProp,
    checkedIcon: CheckedIcon,
    radio,
    label,
    labelPrimary,
    labelSecondary,
    hideControl,
    ...rest
  } = props;

  const ownerState = {
    ...props,
  };
  const classes = useUtilityClasses(ownerState);

  /**
   * Checkbox control
   */
  const [checkedState, setCheckedState] = useControlled({
    controlled: checkedProp,
    default: Boolean(defaultChecked),
    name: 'SwitchChip',
    state: 'checked',
  });

  /**
   * Radio control
   */
  const radioGroup = useRadioGroup();

  let checked = radio ? checkedProp : checkedState;
  let name = nameProp;

  if (radioGroup) {
    if (typeof checked === 'undefined') {
      checked = radioGroup.value === props.value;
    }
    if (typeof name === 'undefined') {
      name = radioGroup.name;
    }
  }

  const onChange = useCallback(
    (event, eventChecked) => {
      onChangeProp?.(event, eventChecked);

      if (radio) {
        radioGroup?.onChange?.(event, eventChecked);
      } else {
        setCheckedState(eventChecked);
      }
    },
    [onChangeProp, radio, setCheckedState, radioGroup],
  );

  return (
    <Root
      ownerState={ownerState}
      className={clsx(className, classes?.root, {
        [classes?.checked]: checked,
        [classes?.fullWidth]: fullWidth,
        [classes?.inactive]: inactive,
        [classes?.hideControl]: hideControl,
      })}
      labelPlacement={radio && !labelPlacementProp ? 'end' : 'start'}
      control={
        !radio ? (
          <Checkbox
            name={name}
            icon={<Check className={classes?.checkIcon} />}
            checkedIcon={<Check className={classes?.checkIcon} />}
            className={classes?.control}
            checked={checked}
            onChange={onChange}
          />
        ) : (
          <Radio
            name={name}
            icon={<RadioIcon className={classes?.icon} />}
            checkedIcon={
              CheckedIcon === 'check' ? (
                <Check className={classes?.checkIcon} />
              ) : (
                <RadioIcon checked className={classes?.icon} />
              )
            }
            className={classes?.controlRadio}
            checked={checked}
            onChange={onChange}
          />
        )
      }
      label={
        label || (
          <>
            {labelPrimary}
            {labelSecondary && (
              <Typography
                component="span"
                className={classes?.labelSecondary}
                variant="caption"
              >
                {labelSecondary}
              </Typography>
            )}
          </>
        )
      }
      {...rest}
    />
  );
});

SwitchChip.propTypes = {
  inactive: PropTypes.bool,
  className: PropTypes.string,
  classes: PropTypes.object,
  defaultChecked: PropTypes.bool,
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  value: PropTypes.any,
  labelPlacement: PropTypes.string,
  fullWidth: PropTypes.bool,
  name: PropTypes.string,
  radio: PropTypes.bool,
  label: PropTypes.node,
  checkedIcon: PropTypes.string,
  labelPrimary: PropTypes.node,
  labelSecondary: PropTypes.node,
  hideControl: PropTypes.bool,
};

export default SwitchChip;
